import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { MenuItem } from "../common/types/AllTypes";
import IAuthContext from "../interfaces/IAuthContext";
import { IMenu } from "../interfaces/IMenu";
import INewPassword from "../interfaces/INewPassword";
import IResportProblem from "../interfaces/IReportProblem";
import { IResponse } from "../interfaces/IResponse";
import { IUser, IUserInformation, IUserSession } from "../interfaces/IUser";
import { BisAPI } from "../services/apis";
import { ChangePassword, ReportProblem, SignIn, SignOut } from "../services/auth";
import { getMenuByUserID, getUserByID } from "../services/user";



// Provider's Properties
interface Props {
    children: any;
}

// Context
const AuthContext = createContext<IAuthContext>({
    signed: false,
    user: undefined,
    loading: true,
    menuFixed: true,
    menuCollapsed: false,
    itemSelected: undefined,
    userInformation: undefined,
    menuList: [],
    menuPlane: [],
    search: '',
    menuSearch: [],
    setMenuFixed: () => { },
    setMenuCollapsed: () => { },
    setItemSelected: () => { },
    signIn: () => new Promise(() => { }),
    signOut: () => new Promise(() => { }),
    changePassword: () => new Promise(() => { }),
    reportProblem: () => new Promise(() => { }),
    searchText: () => { },
    setUserInformation: () => { },
});

const AuthProvider: React.FC<Props> = ({ children }) => {


    // States
    const [user, setUser] = useState<IUser>();
    const [loading, setLoading] = useState<boolean>(true);
    const [menuCollapsed, setMenuCollapsed] = useState<boolean>(false);
    const [menuFixed, setMenuFixed] = useState<boolean>(true);
    const [itemSelected, setItemSelected] = useState<IMenu | undefined>();
    const [menuList, setMenuList] = useState<IMenu[]>([]);
    const [userInformation, setUserInformation] = useState<IUserInformation>();
    const [search, setSearch] = useState<string>('');
    const [menuSearch, setMenuSearch] = useState<MenuItem[]>([]);
    const [menus, setMenus] = useState<MenuItem[]>([]);

    // Ref
    const menuPlane = useRef<IMenu[]>([]);

    // Methods
    const bringUserInfo = (userId: number): Promise<any> => {
        return new Promise<any>((resolve, reject) => {
            getUserByID(userId).then((response) => {
                setUserInformation(response.data);
            }).then(() => {
                return getMenuByUserID(userId).then((response) => {
                    setMenuList(response.data);
                });
            })
                .finally(() => {
                    resolve("DONE")
                })
        })
    }

    function getItem(label: React.ReactNode, key: React.Key, icon?: React.ReactNode, children?: MenuItem[], type?: 'group', className?: string): MenuItem {
        return { key, icon, children, label, type, className, title: label } as MenuItem;
    }

    const loadStoragedData = () => {
        const storedUser = JSON.parse(localStorage.getItem('@auth:user') || 'null');
        if (!!storedUser) {
            BisAPI.defaults.headers.common['usuarioToken'] = storedUser.usuarioToken;
            setUser(storedUser as IUser);
        }
    }



    /// Generate MenuPlane
    const getItemPlane = (menuItem: IMenu) => {
        if (menuItem.menuHijos != null && menuItem.menuHijos.length > 0) {
            menuItem.menuHijos.forEach(item => {
                getItemPlane(item);
            })
        } else {
            if (!menuPlane.current.includes(menuItem)) {
                menuPlane.current.push(menuItem);
            }
        }
    }
    const generateMenuPlane = () => {
        menuList.forEach(menu => {
            getItemPlane(menu);
        })
    }
    //////////////


    /// Generate Menu
    const getSigleMenu = (menu: IMenu): MenuItem => {
        let menuItem: MenuItem;

        if (menu.menuHijos == null || menu.menuHijos?.length == 0) {
            menuItem = getItem(menu.nombre, menu.menuID, <i className={menu.icono + " iconBigger"}></i>)
            return menuItem;
        } else {
            menuItem = getItem(menu.nombre, menu.menuID, <i className={menu.icono + " iconBigger"}></i>, menu.menuHijos!.map((item) => {
                return getSigleMenu(item)
            }))
        }

        return menuItem;
    }

    const generateMenu = (menuListParam: IMenu[]) => {
        const listMenu: MenuItem[] = [];

        menuListParam.forEach((menu) => {
            listMenu.push(getSigleMenu(menu));
        })

        setMenus(listMenu);
        setMenuSearch(listMenu);
    }
    //////


    /// Search Menu
    const searchMenu = (menuListParam: IMenu[], label: string) => {
        const listMenu: MenuItem[] = [];

        menuListParam.forEach((menu) => {
            listMenu.push(searchgetItemPlane(menu, label));
        })

        setMenuSearch(listMenu.filter(menu => menu != null));
    }

    const searchgetItemPlane = (menu: IMenu, label: string) => {
        let menuItem: MenuItem;

        if (menu.menuHijos == null || menu.menuHijos?.length == 0) {
            menuItem = menu.nombre.toLowerCase().includes(label.toLowerCase()) ? getItem(menu.nombre, menu.menuID, <i className={menu.icono + " iconBigger"}></i>) : null;
            return menuItem;
        } else {
            menuItem = getItem(menu.nombre, menu.menuID, <i className={menu.icono + " iconBigger"}></i>, menu.menuHijos!.map((item) => {
                return searchgetItemPlane(item, label)
            })
            )
        }
        return (menuItem as any)?.children.some((child: any) => child != null) || (menuItem as any).label.toLowerCase().includes(label.toLowerCase()) ? menuItem : null;
    }
    /////


    const searchText = (value: string) => {
        setSearch(value);
        if (!!value) {
            searchMenu(menuList, value);
        } else {
            setMenuSearch(menus);
        }
    }


    // Effects
    useEffect(() => {
        loadStoragedData();
        if (!!!user) setLoading(false);
    }, []);

    useEffect(() => {
        if (!!user) {
            setLoading(true)
            bringUserInfo(user!.usuarioID).then(() => { }).finally(() => setLoading(false))
        }
    }, [, user]);

    useEffect(() => {
        generateMenuPlane();
    }, [menuList]);

    useEffect(() => {
        generateMenu(menuList);
    }, [menuList]);

    // Handles
    const signIn = (user: string, pass: string): Promise<IResponse> => {
        const userParams: IUserSession = {
            nombreUsuario: user,
            contrasena: pass,
            dispositivo: navigator.userAgent,
            plataforma: navigator.userAgent,
            version: navigator.userAgent
        }

        return new Promise((resolve, reject) => {
            SignIn(userParams)
                .then((response) => {
                    BisAPI.defaults.headers.common['usuarioToken'] = response.data.usuarioToken;
                    localStorage.setItem('@auth:user', JSON.stringify(response.data));
                    setUser(response.data!);
                    resolve(response)
                })
                .catch((response) => {
                    reject(response?.msg);
                })
        });
    }

    const signOut = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            SignOut()
                .then((response) => {
                    localStorage.clear();
                    setUser(undefined);
                    resolve(response.msg!);
                })
                .catch((response) => {
                    reject(response?.msg);
                })
        })
    }

    const reportProblem = (errorTitle: string, errorDescription: string): Promise<string> => {

        const problem: IResportProblem = {
            usuarioID: user!.usuarioID,
            errorNombre: errorTitle,
            errorDescripcion: errorDescription
        }

        return new Promise((resolve, reject) => {
            ReportProblem(problem)
                .then((response) => {
                    resolve(response.msg!)
                })
                .catch((response) => {
                    reject(response?.msg);
                })
        })
    }

    const changePassword = (oldPassword: string, newPassword: string): Promise<string> => {

        const newPassWordParams: INewPassword = {
            usuarioID: user!.usuarioID,
            contrasena: oldPassword,
            contrasenaNueva: newPassword
        }

        return new Promise((resolve, reject) => {
            ChangePassword(newPassWordParams)
                .then((response) => {
                    resolve(response.msg!)
                })
                .catch((response) => {
                    reject(response?.msg);
                })
        })
    }


    return (
        <AuthContext.Provider
            value={{
                signed: !!user,
                loading: loading,
                user: user,
                menuFixed,
                menuCollapsed,
                userInformation,
                menuList,
                itemSelected,
                menuPlane,
                search,
                menuSearch,
                setMenuFixed,
                setMenuCollapsed,
                setItemSelected,
                signIn,
                signOut,
                changePassword,
                reportProblem,
                searchText,
                setUserInformation
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

// Hook Context
export const useAuth = () => {
    const context = useContext(AuthContext);
    return context;
}

export default AuthProvider;
